import React, { useCallback, useEffect, useState } from 'react';
import { SearchIcon, TruckIcon, StarIcon, ArrowRightIcon, MapPinIcon, ClockIcon, PawPrintIcon, FlowerIcon, WavesIcon } from 'lucide-react';
interface HeroSectionProps {
  onNavigate: (page: 'shop') => void;
}
const categories = [{
  id: 'pets',
  label: 'Pets',
  count: '2,400+ Products',
  subtitle: 'Dogs, Cats, Birds, Fish & More',
  image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1920&q=85',
  imagePosition: 'center 25%',
  thumb: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=300&q=80',
  accentFrom: 'from-brand-blue-300',
  accentTo: 'to-cyan-300',
  accentText: 'text-brand-blue-300',
  borderHover: 'border-brand-blue-400',
  bgHover: 'bg-brand-blue-400/20',
  icon: PawPrintIcon
}, {
  id: 'gardens',
  label: 'Gardens',
  count: '800+ Products',
  subtitle: 'Fertilisers, Soil, Seeds & Tools',
  image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=1920&q=85',
  imagePosition: 'center 40%',
  thumb: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=300&q=80',
  accentFrom: 'from-brand-green-300',
  accentTo: 'to-emerald-300',
  accentText: 'text-brand-green-300',
  borderHover: 'border-brand-green-400',
  bgHover: 'bg-brand-green-400/20',
  icon: FlowerIcon
}, {
  id: 'pools',
  label: 'Pools',
  count: '350+ Products',
  subtitle: 'Chlorine, Pumps, Testing & Accessories',
  image: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=1920&q=85',
  imagePosition: 'center 50%',
  thumb: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=300&q=80',
  accentFrom: 'from-cyan-300',
  accentTo: 'to-sky-300',
  accentText: 'text-cyan-300',
  borderHover: 'border-cyan-400',
  bgHover: 'bg-cyan-400/20',
  icon: WavesIcon
}];
export function HeroSection({
  onNavigate
}: HeroSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  // Auto-cycle when not hovering
  useEffect(() => {
    if (isHovering) return;
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % categories.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isHovering]);
  const handleCategoryHover = useCallback((index: number) => {
    setIsHovering(true);
    setActiveIndex(index);
  }, []);
  const handleCategoryLeave = useCallback(() => {
    setIsHovering(false);
  }, []);
  const active = categories[activeIndex];
  return <section className="relative min-h-[calc(100vh-200px)] flex items-center overflow-hidden" data-id="element-151">
      {/* Background images — all preloaded, opacity-controlled */}
      {categories.map((cat, index) => <div key={cat.id} className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out" style={{
      opacity: activeIndex === index ? 1 : 0
    }} data-id="element-152">
          <div className="absolute inset-0 bg-cover transition-transform duration-[8000ms] ease-linear" style={{
        backgroundImage: `url(${cat.image})`,
        backgroundPosition: cat.imagePosition,
        transform: activeIndex === index ? 'scale(1.08)' : 'scale(1)'
      }} data-id="element-153"></div>
        </div>)}

      {/* Cinematic overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-gray-950/95 via-brand-blue-900/80 to-gray-950/50" data-id="element-154"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-gray-950/70 via-transparent to-gray-950/30" data-id="element-155"></div>
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(ellipse at 75% 50%, transparent 30%, rgba(0,0,0,0.5) 100%)'
    }} data-id="element-156"></div>

      {/* Film grain */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
      backgroundSize: '128px 128px'
    }} data-id="element-157"></div>

      {/* Floating FREE Drop-off Badge — top right */}
      <div className="absolute top-6 right-6 lg:top-8 lg:right-8 z-30 animate-bounce-gentle" data-id="element-158">
        <div className="bg-brand-green text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 border-2 border-brand-green-300/30" data-id="element-159">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0" data-id="element-160">
            <TruckIcon className="w-5 h-5" data-id="element-161" />
          </div>
          <div data-id="element-162">
            <div className="font-heading font-extrabold text-sm leading-tight" data-id="element-163">
              FREE Drop-off
            </div>
            <div className="text-brand-green-100 text-xs font-medium" data-id="element-164">
              Within 5km of any store
            </div>
          </div>
        </div>
      </div>

      <div className="container-wide relative z-10 py-12 lg:py-20" data-id="element-165">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center" data-id="element-166">
          {/* Left Content */}
          <div className="lg:col-span-7" data-id="element-167">
            {/* Trust badge */}
            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 mb-8 animate-fade-in" data-id="element-168">
              <div className="flex items-center gap-0.5" data-id="element-169">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-170" />)}
              </div>
              <div className="w-px h-4 bg-white/30" data-id="element-171"></div>
              <span className="text-sm font-semibold text-white" data-id="element-172">
                Rated 4.6/5 by WA Locals
              </span>
            </div>

            {/* Dynamic headline — accent colors change with active category */}
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem] text-white leading-[1.05] mb-6 tracking-tight animate-slide-up" style={{
            animationDelay: '0.1s'
          }} data-id="element-173">
              Everything for your{' '}
              <span className={`transition-colors duration-700 ${activeIndex === 0 ? 'text-brand-blue-300' : 'text-white/50'}`} data-id="element-174">
                Pets
              </span>
              ,{' '}
              <span className={`transition-colors duration-700 ${activeIndex === 1 ? 'text-brand-green-300' : 'text-white/50'}`} data-id="element-175">
                Gardens
              </span>
              {' & '}
              <span className={`transition-colors duration-700 ${activeIndex === 2 ? 'text-cyan-300' : 'text-white/50'}`} data-id="element-176">
                Pools
              </span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 lg:mb-10 max-w-2xl leading-relaxed animate-slide-up" style={{
            animationDelay: '0.2s'
          }} data-id="element-177">
              Six WA stores. Real advice. Real service.
              <span className="text-white font-semibold" data-id="element-178">
                {' '}
                Shop online or visit us in-store today.
              </span>
            </p>

            {/* Search Bar */}
            <div className="bg-white/95 backdrop-blur-sm p-2 md:p-2.5 rounded-2xl shadow-2xl flex flex-col sm:flex-row gap-2 max-w-2xl animate-slide-up ring-1 ring-white/20" style={{
            animationDelay: '0.3s'
          }} data-id="element-179">
              <div className="flex-1 relative flex items-center" data-id="element-180">
                <SearchIcon className="absolute left-4 w-5 h-5 text-gray-400" data-id="element-181" />
                <input type="text" placeholder="What are you looking for today?" className="w-full pl-12 pr-4 py-3.5 sm:py-4 bg-transparent text-gray-900 text-base sm:text-lg focus:outline-none placeholder-gray-400" data-id="element-182" />
              </div>
              <button onClick={() => onNavigate('shop')} className="btn-primary py-3.5 sm:py-4 px-6 sm:px-8 text-base sm:text-lg shadow-blue-glow whitespace-nowrap flex items-center justify-center gap-2" data-id="element-183">
                Shop Now <ArrowRightIcon className="w-5 h-5" data-id="element-184" />
              </button>
            </div>

            {/* Popular searches */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-6 lg:mt-8 animate-slide-up" style={{
            animationDelay: '0.4s'
          }} data-id="element-185">
              <span className="text-white/60 text-sm font-medium" data-id="element-186">
                Popular:
              </span>
              {['Dog Food', 'Flea & Tick', 'Pool Chlorine', 'Fertilisers'].map(term => <button key={term} onClick={() => onNavigate('shop')} className="px-3 sm:px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-white text-xs sm:text-sm font-medium hover:bg-white/20 hover:border-white/30 transition-all" data-id="element-187">
                    {term}
                  </button>)}
            </div>

            {/* MOBILE Feature Cards */}
            <div className="grid grid-cols-3 gap-3 mt-8 lg:hidden animate-slide-up" style={{
            animationDelay: '0.5s'
          }} data-id="element-188">
              <div className="bg-white/10 backdrop-blur-xl rounded-xl p-3 border border-white/20 text-center" data-id="element-189">
                <TruckIcon className="w-5 h-5 text-brand-green-300 mx-auto mb-1.5" data-id="element-190" />
                <div className="text-white font-bold text-xs leading-tight" data-id="element-191">
                  FREE Drop-off
                </div>
                <div className="text-gray-400 text-[10px]" data-id="element-192">Within 5km</div>
              </div>
              <div className="bg-white/10 backdrop-blur-xl rounded-xl p-3 border border-white/20 text-center" data-id="element-193">
                <MapPinIcon className="w-5 h-5 text-brand-blue-300 mx-auto mb-1.5" data-id="element-194" />
                <div className="text-white font-bold text-xs leading-tight" data-id="element-195">
                  6 Stores
                </div>
                <div className="text-gray-400 text-[10px]" data-id="element-196">Open 7 Days</div>
              </div>
              <div className="bg-white/10 backdrop-blur-xl rounded-xl p-3 border border-white/20 text-center" data-id="element-197">
                <ClockIcon className="w-5 h-5 text-cyan-300 mx-auto mb-1.5" data-id="element-198" />
                <div className="text-white font-bold text-xs leading-tight" data-id="element-199">
                  20+ Years
                </div>
                <div className="text-gray-400 text-[10px]" data-id="element-200">Serving WA</div>
              </div>
            </div>
          </div>

          {/* Right Side — Interactive Category Cards */}
          <div className="hidden lg:flex lg:col-span-5 flex-col gap-4 animate-slide-up" style={{
          animationDelay: '0.5s'
        }} onMouseLeave={handleCategoryLeave} data-id="element-201">
            {categories.map((cat, index) => {
            const isActive = activeIndex === index;
            const Icon = cat.icon;
            return <div key={cat.id} className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 border-2 ${isActive ? `${cat.borderHover} bg-white/15 shadow-lg scale-[1.02]` : 'border-white/10 bg-white/5 hover:bg-white/10'}`} onMouseEnter={() => handleCategoryHover(index)} onClick={() => onNavigate('shop')} data-id="element-202">
                  <div className="flex items-center gap-5 p-5" data-id="element-203">
                    {/* Thumbnail */}
                    <div className={`w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 ring-2 transition-all duration-500 ${isActive ? `ring-white/40 shadow-lg` : 'ring-white/10'}`} data-id="element-204">
                      <img src={cat.thumb} alt={cat.label} className={`w-full h-full object-cover transition-transform duration-700 ${isActive ? 'scale-110' : 'scale-100'}`} data-id="element-205" />
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0" data-id="element-206">
                      <div className="flex items-center gap-2 mb-1" data-id="element-207">
                        <Icon className={`w-5 h-5 transition-colors duration-500 ${isActive ? cat.accentText : 'text-white/50'}`} data-id="element-208" />
                        <h3 className={`font-heading font-extrabold text-2xl transition-colors duration-500 ${isActive ? 'text-white' : 'text-white/70'}`} data-id="element-209">
                          {cat.label}
                        </h3>
                      </div>
                      <p className={`text-sm font-medium transition-colors duration-500 ${isActive ? cat.accentText : 'text-gray-500'}`} data-id="element-210">
                        {cat.count}
                      </p>
                      <p className={`text-xs mt-0.5 transition-colors duration-500 ${isActive ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-211">
                        {cat.subtitle}
                      </p>
                    </div>

                    {/* Arrow */}
                    <ArrowRightIcon className={`w-5 h-5 flex-shrink-0 transition-all duration-500 ${isActive ? 'text-white translate-x-0 opacity-100' : 'text-white/30 -translate-x-2 opacity-0'}`} data-id="element-212" />
                  </div>

                  {/* Active indicator bar */}
                  <div className={`h-1 transition-all duration-500 ${isActive ? `${cat.bgHover} w-full` : 'bg-transparent w-0'}`} data-id="element-213"></div>
                </div>;
          })}

            {/* Mini stats row below cards */}
            <div className="grid grid-cols-2 gap-3 mt-1" data-id="element-214">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10 text-center" data-id="element-215">
                <div className="font-heading font-extrabold text-xl text-white" data-id="element-216">
                  20+
                </div>
                <div className="text-gray-500 text-xs font-medium" data-id="element-217">
                  Years Serving WA
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10 text-center" data-id="element-218">
                <div className="font-heading font-extrabold text-xl text-white" data-id="element-219">
                  6
                </div>
                <div className="text-gray-500 text-xs font-medium" data-id="element-220">
                  Perth Locations
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category indicators (replacing slide dots) */}
      <div className="absolute bottom-6 lg:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20" data-id="element-221">
        {categories.map((cat, index) => <button key={cat.id} onClick={() => {
        setActiveIndex(index);
        setIsHovering(true);
      }} className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-500 ${activeIndex === index ? 'bg-white/20 backdrop-blur-sm border border-white/30' : 'bg-transparent border border-transparent hover:bg-white/10'}`} aria-label={`Show ${cat.label}`} data-id="element-222">
            <div className={`w-2 h-2 rounded-full transition-all duration-500 ${activeIndex === index ? 'bg-white scale-100' : 'bg-white/40 scale-75'}`} data-id="element-223"></div>
            <span className={`text-xs font-semibold transition-all duration-500 ${activeIndex === index ? 'text-white' : 'text-white/40'}`} data-id="element-224">
              {cat.label}
            </span>
          </button>)}
      </div>
    </section>;
}